import type { MutableRefObject } from 'react';
import { Stack } from '@etg/wings';
import { useProperty, useSearch } from '@eti/providers';
import { css, keyframes } from '@eti/styles';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { quickSortType } from '../../../../constants/quickSortConstants';
import { removeUndesiredTripsAndTags } from '../../utils/resultUtils';
import MobileAppBanner from '../../../../common/mobile-app-banner/MobileAppBanner';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import type { NearbyDatePricesType, TripsType, TripType } from '../../models';
import type { NearbyPricesWithSelectedTripVersionEnumTypesValues } from './nearbyDatesPrices/components/NearbyDatesPrices';
import { useResultPageContext } from './contexts/ResultPageContext';
import { ResultTripCards } from './resultTripCards/ResultTripCards';
import ResultTripCard from './resultTripCards/ResultTripCard';
import { SelectedTripCard } from './resultTripCards/SelectedTripCard';
import { RESULT_TRIPS } from './resultTripCards/constants';
import NearbyDatesPrices, {
  NearbyPricesWithSelectedTripVersionEnumTypes,
} from './nearbyDatesPrices/components/NearbyDatesPrices';
import { getFlightWhenInTripCampaigns, isAnyRecommendedFlights } from './utils/trip';

const resultsMoveAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translateY(0);
  }
  10% {
    opacity: 0.5;
    transform: translateY(10px);
  }
  20% {
    opacity: 0.25;
    transform: translateY(20px);
  }
  30% {
    opacity: 0.25;
    transform: translateY(30px);
  }
  40% {
    opacity: 0.25;
    transform: translateY(40px);
  }
  50% {
    opacity: 0;
    transform: translateY(50px);
  }
  60% {
    opacity: 0.25;
    transform: translateY(35px);
  }
  70% {
    opacity: 0.5;
    transform: translateY(15px);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const flightResultsInnerWrapperStyles = (shouldShowResultsLoader: boolean) => css`
  animation: ${resultsMoveAnimation} 0.8s;
  ${shouldShowResultsLoader && 'opacity: 0.5; pointer-events: none;'}
`;

const mobileAppBannerStyles = css`
  margin-bottom: 16px;
`;

interface ResultsTripCardsSectionProps {
  flightResultsWrapperRef: MutableRefObject<null>;
  isNearbyDatesLoading: boolean;
  nearbyDatePrices: NearbyDatePricesType;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
  shouldShowNearbyDatesPrices: boolean;
  tripsExcludingSponsored: TripsType;
}

const ResultsTripCardsSection = ({
  flightResultsWrapperRef,
  isNearbyDatesLoading,
  nearbyDatePrices,
  searchFormWatchRef,
  setValueRef,
  shouldShowNearbyDatesPrices,
  tripsExcludingSponsored,
}: ResultsTripCardsSectionProps) => {
  const {
    handleSearch,
    searchData,
    selectedTrip,
    setIsItineraryDrawerSelfTransferModalOpen,
    shouldShowResultsLoader,
    setIsSelfTransferModalOpen,
  } = useResultPageContext();
  const { currentSearchData } = useSearch();
  const { p } = useProperty();
  const isMobileAppPromoBannerEnabled = p('IbeClient.MobileAppPromoBanner.Enabled');
  const nearbyPricesWithSelectedTripVersion: NearbyPricesWithSelectedTripVersionEnumTypesValues = p(
    'Result.Page.NearbyPrices.WithSelectedTrip.Version',
  );
  const { flights, sponsoredTrips, tripCampaigns } = {
    ...searchData,
  };
  const { selectedSortType } = currentSearchData || {};
  const sponsoredTripsUndesiredRemoved = removeUndesiredTripsAndTags(sponsoredTrips);

  const getRecommendedTrip =
    flights &&
    flights[0] &&
    isAnyRecommendedFlights(flights, selectedSortType) &&
    Boolean(getFlightWhenInTripCampaigns(flights[0], tripCampaigns))
      ? flights[0]
      : null;

  const shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard =
    shouldShowNearbyDatesPrices &&
    selectedTrip &&
    nearbyPricesWithSelectedTripVersion === NearbyPricesWithSelectedTripVersionEnumTypes.A;

  const removeSelectedTrip = (trips: TripsType, selectedTripToBeRemoved: TripType) => {
    return selectedTripToBeRemoved
      ? trips.filter((trip) => trip?.selectionKey !== selectedTripToBeRemoved.selectionKey)
      : trips;
  };

  const nearbyDatesPricesComponent = (
    <NearbyDatesPrices
      handleSearch={handleSearch}
      hasSelectedTrip={selectedTrip}
      isLoading={isNearbyDatesLoading}
      nearbyDatePrices={nearbyDatePrices}
      searchFormWatchRef={searchFormWatchRef}
      setValueRef={setValueRef}
    />
  );

  return (
    <div
      className={flightResultsInnerWrapperStyles(shouldShowResultsLoader)}
      key={selectedSortType}
    >
      <Stack>
        {selectedTrip && <SelectedTripCard />}

        {/* Selected trip (from meta) Nearby dates below Selected trip card, and with RECOMMENDED filter */}
        {selectedSortType === quickSortType.RECOMMENDED &&
          shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard &&
          nearbyDatesPricesComponent}

        {selectedSortType === quickSortType.RECOMMENDED && (
          <Stack>
            {getRecommendedTrip && (
              <ResultTripCard
                key={getRecommendedTrip.selectionKey}
                resultTripType={RESULT_TRIPS.RECOMMENDED}
                setIsItineraryDrawerSelfTransferModalOpen={
                  setIsItineraryDrawerSelfTransferModalOpen
                }
                setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                trip={getRecommendedTrip}
                tripCampaign={getFlightWhenInTripCampaigns(getRecommendedTrip)}
              />
            )}
            {sponsoredTripsUndesiredRemoved && (
              <ResultTripCards
                resultTripType={RESULT_TRIPS.SPONSORED}
                setIsItineraryDrawerSelfTransferModalOpen={
                  setIsItineraryDrawerSelfTransferModalOpen
                }
                setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                trips={removeSelectedTrip(sponsoredTripsUndesiredRemoved, selectedTrip)}
              />
            )}
          </Stack>
        )}
        <div ref={flightResultsWrapperRef}>
          {isMobileAppPromoBannerEnabled && flights && flights.length > 1 && (
            <MobileAppBanner className={mobileAppBannerStyles} isStartPage={false} />
          )}

          {/* Selected trip (from meta) Nearby dates below Selected trip card, and without RECOMMENDED filter */}
          {!(selectedSortType === quickSortType.RECOMMENDED) &&
            shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard &&
            nearbyDatesPricesComponent}

          <ResultTripCards
            setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
            setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
            tripCampaigns={tripCampaigns}
            trips={removeSelectedTrip(tripsExcludingSponsored, selectedTrip)}
          />
        </div>
      </Stack>
    </div>
  );
};

export default ResultsTripCardsSection;
